import { Dispatch, SetStateAction, useEffect, useMemo, useRef } from 'react';

import { enqueueSnackbar } from 'notistack';

import { useChartData } from '@/components/Chart/queries/useChartData';
import { findSeriesBasedOnKey } from '@/components/Chart/utils/findSeriesBasedOnKey';
import { getShortTitle } from '@/components/Chart/utils/getShortTitle';
import { isTemplateValid } from '@/components/Chart/utils/isTemplateValid';
import { takeForwardCurveData } from '@/components/Chart/utils/takeForwardCurveData';
import {
  INVALID_PERIODS_ERROR,
  NO_DATA_ERROR,
  validateCurveData
} from '@/components/Chart/utils/validateCurveData';
import { TemplateChartSeries } from '@/pages/Markets/MarketsComponents/types';

import { parseDataToChartSeries } from '../utils/parseDataToChartSeries';

const useForwardCurveChartSeries = (
  templateChartSeries: TemplateChartSeries[] | undefined,
  setComparisonTemplateSeries?: Dispatch<SetStateAction<TemplateChartSeries[]>>
) => {
  // Fetch chart data using React Query
  const {
    data: chartData,
    isError: isChartDataError,
    isLoading: isChartDataLoading
  } = useChartData(templateChartSeries, 'curve');

  // Validate symbols and determine error type
  const validationResult = useMemo(() => {
    if (!chartData?.symbols) {
      return { invalidSymbols: [], errorType: null };
    }
    return validateCurveData(chartData.symbols);
  }, [chartData]);

  const { invalidSymbols, errorType } = validationResult;

  const validSymbols = useMemo(() => {
    if (!chartData?.symbols) return [];
    return chartData.symbols.filter(symbol => !invalidSymbols.includes(symbol));
  }, [chartData, invalidSymbols]);

  // Track previous error to prevent duplicate snackbars
  const previousErrorTypeRef = useRef<typeof errorType>(null);

  // Show error notification only once when error type changes
  useEffect(() => {
    if (errorType && errorType !== previousErrorTypeRef.current) {
      const message = errorType === 'no-data' ? NO_DATA_ERROR : INVALID_PERIODS_ERROR;
      enqueueSnackbar(message, {
        variant: 'error',
        autoHideDuration: 5000,
        preventDuplicate: true
      });
      previousErrorTypeRef.current = errorType;
    } else if (!errorType) {
      previousErrorTypeRef.current = null;
    }
  }, [errorType]);

  // Update comparison series when invalid symbols are found
  useEffect(() => {
    if (!setComparisonTemplateSeries || invalidSymbols.length === 0) return;

    setComparisonTemplateSeries(prev =>
      prev.filter(templateSeries => isTemplateValid(templateSeries, invalidSymbols))
    );
  }, [invalidSymbols, setComparisonTemplateSeries]);

  // Transform valid symbols into chart series
  const chartSeries = useMemo(() => {
    if (validSymbols.length === 0) return undefined;

    return validSymbols.flatMap(({ key, metadata, series, transactionDate }, symbolIndex) => {
      const templateSeries = findSeriesBasedOnKey(templateChartSeries, key);
      const shortTitle = getShortTitle(templateSeries);
      const seriesColor = templateSeries?.settings.color;
      const parseableElements = {
        symbolIndex,
        shortTitle,
        key,
        metadata,
        transactionDate,
        color: seriesColor
      };
      return parseDataToChartSeries(series, parseableElements, false, takeForwardCurveData);
    });
  }, [validSymbols, templateChartSeries]);

  return {
    loading: isChartDataLoading || !templateChartSeries,
    error: isChartDataError,
    chartSeries
  };
};

export default useForwardCurveChartSeries;
